import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const aboutDropdownItems = [
  { label: 'Who We Are', to: '/about', icon: 'ri-building-4-line', desc: 'Our story, mission & values' },
  { label: 'Our Culture', to: '/culture', icon: 'ri-heart-line', desc: 'People, pillars & principles' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const aboutRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setAboutOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (aboutRef.current && !aboutRef.current.contains(e.target as Node)) {
        setAboutOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const isAboutActive = location.pathname === '/about' || location.pathname === '/culture';
  const textColor = scrolled ? 'text-gray-800' : 'text-white';
  const hoverColor = scrolled ? 'hover:text-amber-600' : 'hover:text-amber-300';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-white backdrop-blur-xl shadow-sm border-b border-slate-100' : 'bg-[#0d1b2a]/75 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-22 py-2">

          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <div className="flex items-center">
              <img
                src="https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/18dc2a58-51dc-414a-b658-4a632eef1ab9_vectorized360hospnobackground.png?v=3ae9bb5e7d18760fcad351900ce2812a"
                alt="360 Hospitality Group"
                className={`h-16 w-auto object-contain transition-all duration-500 ${scrolled ? '' : 'brightness-0 invert'}`}
              />
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link to="/services" className={`text-sm font-light tracking-wide uppercase transition-colors ${hoverColor} ${textColor}`}>
              Services
            </Link>
            <Link to="/portfolio" className={`text-sm font-light tracking-wide uppercase transition-colors ${hoverColor} ${textColor}`}>
              Portfolio
            </Link>
            <Link to="/performance" className={`text-sm font-light tracking-wide uppercase transition-colors ${hoverColor} ${textColor}`}>
              Performance
            </Link>

            {/* About Dropdown */}
            <div ref={aboutRef} className="relative">
              <button
                onClick={() => setAboutOpen((v) => !v)}
                className={`flex items-center space-x-1 text-sm font-light tracking-wide uppercase transition-colors ${hoverColor} ${
                  isAboutActive ? 'text-amber-500' : textColor
                } cursor-pointer whitespace-nowrap`}
              >
                <span>About</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${aboutOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {/* Dropdown panel */}
              {aboutOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-white rounded-2xl border border-slate-100 overflow-hidden z-50"
                  style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
                >
                  {/* Arrow */}
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-slate-100 rotate-45"></div>

                  <div className="p-2">
                    {aboutDropdownItems.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                          location.pathname === item.to
                            ? 'bg-amber-50 text-amber-700'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <div className={`w-9 h-9 flex items-center justify-center rounded-lg shrink-0 transition-colors ${
                          location.pathname === item.to ? 'bg-amber-100' : 'bg-slate-100 group-hover:bg-amber-50'
                        }`}>
                          <i className={`${item.icon} text-base ${
                            location.pathname === item.to ? 'text-amber-600' : 'text-slate-500 group-hover:text-amber-500'
                          }`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium leading-tight">{item.label}</p>
                          <p className="text-xs text-slate-400 font-light mt-0.5">{item.desc}</p>
                        </div>
                        {location.pathname === item.to && (
                          <div className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></div>
                        )}
                      </Link>
                    ))}
                  </div>

                  {/* Footer hint */}
                  <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
                    <p className="text-xs text-slate-400 font-light text-center">Florida&apos;s Performance-First Partner</p>
                  </div>
                </div>
              )}
            </div>

            <Link to="/contact" className={`text-sm font-light tracking-wide uppercase transition-colors ${hoverColor} ${textColor}`}>
              Contact
            </Link>
            <Link to="/partner" className={`text-sm font-light tracking-wide uppercase transition-colors ${hoverColor} ${textColor}`}>
              Partner With Us
            </Link>
          </div>

          {/* Right Side */}
          <div className="hidden lg:flex items-center space-x-4">
            <button
              className={`w-9 h-9 flex items-center justify-center transition-colors ${hoverColor} ${textColor}`}
              aria-label="Search"
            >
              <i className="ri-search-line text-xl"></i>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center transition-colors ${textColor}`}
            aria-label="Toggle menu"
          >
            <i className={`text-2xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-6 py-4 space-y-1">
            <Link to="/services" className="block py-2.5 text-sm font-light tracking-wide uppercase text-gray-900 hover:text-amber-600">
              Services
            </Link>
            <Link to="/portfolio" className="block py-2.5 text-sm font-light tracking-wide uppercase text-gray-900 hover:text-amber-600">
              Portfolio
            </Link>
            <Link to="/performance" className="block py-2.5 text-sm font-light tracking-wide uppercase text-gray-900 hover:text-amber-600">
              Performance
            </Link>

            {/* Mobile About group */}
            <div className="pt-1 pb-1">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-widest px-0 py-2">About</p>
              <div className="space-y-1 pl-3 border-l-2 border-amber-200">
                {aboutDropdownItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-center space-x-2 py-2 text-sm font-light tracking-wide transition-colors ${
                      location.pathname === item.to ? 'text-amber-600' : 'text-gray-700 hover:text-amber-600'
                    }`}
                  >
                    <i className={`${item.icon} text-base`}></i>
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>

            <Link to="/contact" className="block py-2.5 text-sm font-light tracking-wide uppercase text-gray-900 hover:text-amber-600">
              Contact
            </Link>
            <Link to="/partner" className="block py-2.5 text-sm font-light tracking-wide uppercase text-gray-900 hover:text-amber-600">
              Partner With Us
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
